import { createDropdownOverlayTokens, createInputTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * AutoComplete Component Configuration
 *
 * AutoComplete is an input + suggestions overlay, structurally like select /
 * multiselect. Without these tokens PrimeNG renders it unstyled — a transparent
 * suggestions panel and a clear (X) icon positioned outside the input — so it
 * reuses the same input + dropdown-overlay tokens as multiselect.
 */
export function createAutocomplete(s: SemanticTokens) {
  const inputTokens = createInputTokens(s);
  const dropdownOverlayTokens = createDropdownOverlayTokens(s);
  return {
    root: {
      background: inputTokens.bg,
      disabledBackground: inputTokens.bgDisabled,
      filledBackground: inputTokens.bg,
      filledHoverBackground: inputTokens.bgHover,
      filledFocusBackground: inputTokens.bg,
      borderColor: inputTokens.border,
      hoverBorderColor: inputTokens.border,
      focusBorderColor: inputTokens.borderFocus,
      invalidBorderColor: inputTokens.borderInvalid,
      color: inputTokens.fg,
      disabledColor: inputTokens.fgDisabled,
      placeholderColor: inputTokens.placeholder,
      invalidPlaceholderColor: inputTokens.placeholder,
      shadow: 'none',
      paddingX: inputTokens.base.paddingX,
      paddingY: inputTokens.base.paddingY,
      borderRadius: inputTokens.base.radius,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
      transitionDuration: dropdownOverlayTokens.transition,
    },
    overlay: {
      background: dropdownOverlayTokens.bg,
      borderColor: dropdownOverlayTokens.border,
      borderRadius: dropdownOverlayTokens.radius,
      color: dropdownOverlayTokens.fg,
      shadow: dropdownOverlayTokens.shadow,
    },
    list: { padding: dropdownOverlayTokens.listPadding, gap: dropdownOverlayTokens.listGap },
    option: {
      focusBackground: dropdownOverlayTokens.option.bgFocus,
      selectedBackground: dropdownOverlayTokens.option.bgSelected,
      selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
      color: dropdownOverlayTokens.option.fg,
      focusColor: dropdownOverlayTokens.option.fgFocus,
      selectedColor: dropdownOverlayTokens.option.fgSelected,
      selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
      padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}`,
      borderRadius: dropdownOverlayTokens.option.radius,
      gap: dropdownOverlayTokens.option.gap,
    },
    optionGroup: {
      background: 'transparent',
      color: dropdownOverlayTokens.optionGroup.fg,
      fontWeight: dropdownOverlayTokens.optionGroup.fontWeight,
      padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}`,
    },
    dropdown: {
      width: dropdownOverlayTokens.dropdown.size,
      color: dropdownOverlayTokens.dropdownIconFg,
    },
    chip: { borderRadius: dropdownOverlayTokens.chip.borderRadius },
    emptyMessage: {
      padding: `${dropdownOverlayTokens.emptyMessagePadding} ${dropdownOverlayTokens.emptyMessagePadding}`,
    },
    colorScheme: {
      light: {
        root: {
          background: inputTokens.bg,
          disabledBackground: inputTokens.bgDisabled,
          filledBackground: inputTokens.bg,
          filledHoverBackground: inputTokens.bgHover,
          filledFocusBackground: inputTokens.bg,
          borderColor: inputTokens.border,
          hoverBorderColor: inputTokens.border,
          focusBorderColor: inputTokens.borderFocus,
          invalidBorderColor: inputTokens.borderInvalid,
          color: inputTokens.fg,
          disabledColor: inputTokens.fgDisabled,
          placeholderColor: inputTokens.placeholder,
          invalidPlaceholderColor: inputTokens.placeholder,
        },
        overlay: {
          background: dropdownOverlayTokens.bg,
          borderColor: dropdownOverlayTokens.border,
          color: dropdownOverlayTokens.fg,
        },
        option: {
          focusBackground: dropdownOverlayTokens.option.bgFocus,
          selectedBackground: dropdownOverlayTokens.option.bgSelected,
          selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
          color: dropdownOverlayTokens.option.fg,
          focusColor: dropdownOverlayTokens.option.fgFocus,
          selectedColor: dropdownOverlayTokens.option.fgSelected,
          selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
        },
      },
    },
    css: () => `
      /* Keep the clear (X) icon inside the input */
      .p-autocomplete-clear-icon {
        right: ${inputTokens.base.paddingX};
      }
      /* When a dropdown chevron is present, keep the clear (X) icon clear of it */
      .p-autocomplete:has(.p-autocomplete-dropdown) .p-autocomplete-clear-icon {
        right: calc(${dropdownOverlayTokens.dropdown.size} + ${dropdownOverlayTokens.triggerPaddingRight} + 0.25rem);
      }
      /* Truncate long suggestions so the overlay doesn't overflow its width */
      .p-autocomplete-option {
        min-width: 0;
      }
      .p-autocomplete-option,
      .p-autocomplete-option > * {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  };
}
export const autocomplete = createAutocomplete(SEMANTIC);
